import {
  Container,
  ContainerProps,
  Paper,
  Box,
  Typography,
  Button,
} from "@mui/material";
import React, { useState, useMemo, useRef } from "react";
import { LayoutItem } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
// @ts-expect-error // fix error with import
// eslint-disable-next-line import/no-unresolved
import { Responsive, WidthProvider } from "react-grid-layout/legacy";
import {
  CLIENT_PROFILE_LAYOUT_SETUP_CARDS_DATA,
  ProfileCardPosition,
  ProfileCardType,
} from "./utils";
import { ProfileIconBlock } from "../../coach-clients/ProfileIconBlock";
import ClientDetailsCardHeader from "../../coach-clients/ClientDetailsCardHeader";
import makeStyles from "@mui/styles/makeStyles";
import clsx from "clsx";
import { ClientDialog } from "./ClientDialog";
import { useQueryParams } from "../../../hooks/useQueryParams";
import { ProfilePreview } from "./ProfilePreview";
import { PreviewType, usePreview } from "../../../hooks/usePreview";
import { useCurrentWorkspace } from "../../../hooks/useCurrentWorkspace";
import LoadingActionButtonWithStatus from "../../button/LoadingActionButtonWithStatus";
import { useUpdateProfileCardsPosition } from "./mutations/UpdateProfileCardsPosition";
import { deepCompare } from "../../editor/utils/common";
import { ClientInfoDto } from "@growth-machine-llc/stridist-api-client";
import {
  Eye,
  RotateCcw,
  X,
  GripVertical,
  ChevronsLeftRight,
} from "lucide-react";
import { useToastAlert } from "../../app/ToastAlert/ToastAlertProvider";
import { lighten } from "@mui/material/styles";
import { useNavigationGuard } from "../../../hooks/useNavigationGuard";

const normalizeLayoutForCompare = (layout: LayoutItem[]) => {
  const sortedByPosition = [...layout].sort(
    (a, b) => a.y - b.y || a.x - b.x || a.i.localeCompare(b.i),
  );

  const uniqueRows = Array.from(
    new Set(sortedByPosition.map((item) => item.y)),
  );
  const minRowSpan = sortedByPosition.reduce(
    (acc, item) => Math.min(acc, item.h ?? 1),
    Number.POSITIVE_INFINITY,
  );
  const rowStep = Number.isFinite(minRowSpan) ? minRowSpan : 1;

  const rowMap = new Map(uniqueRows.map((row, idx) => [row, idx * rowStep]));

  const compacted = sortedByPosition.map((item) => ({
    ...item,
    y: rowMap.get(item.y) ?? item.y,
  }));

  return compacted.sort((a, b) => a.i.localeCompare(b.i));
};

const areLayoutsEqual = (a: LayoutItem[], b: LayoutItem[]) => {
  if (a.length !== b.length) return false;

  const normalizedA = normalizeLayoutForCompare(a);
  const normalizedB = normalizeLayoutForCompare(b);

  return normalizedA.every((item, idx) => {
    const compareTo = normalizedB[idx];
    return (
      compareTo &&
      item.i === compareTo.i &&
      item.x === compareTo.x &&
      item.y === compareTo.y &&
      item.w === compareTo.w
    );
  });
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    margin: theme.spacing(2.5, "auto"),
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      margin: theme.spacing(1),
      padding: theme.spacing(1),
    },
  },
  actionsWrapper: {
    width: "100%",
    maxWidth: 1200,
    display: "flex",
    justifyContent: "center",
    paddingLeft: theme.spacing(2),
  },
  actionsBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    padding: theme.spacing(0, 2),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(0, 1),
    },
  },
  buttonText: {
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },

  leftActions: {
    display: "flex",
    gap: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      gap: theme.spacing(1),
    },
  },
  rightActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      gap: theme.spacing(1),
    },
  },

  textBtn: {
    textTransform: "none",
    color: theme.palette.text.secondary,
    fontSize: "14px",
    fontWeight: 600,
    padding: 0,
    minWidth: "auto",
    "&:hover": {
      backgroundColor: "transparent",
      color: theme.palette.secondary.main,
    },
  },
  btnDiscard: {
    textTransform: "none",
    color: theme.palette.primary.main,
    fontWeight: 500,
    gap: theme.spacing(0.5),
    minWidth: "auto",
    "&.Mui-disabled": {
      opacity: 0.5,
      color: theme.palette.progress.red,
    },
  },
  btnSave: {
    textTransform: "none",
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    borderRadius: `${theme.spacing(1)} !important`,
    padding: `${theme.spacing(1, 2)} !important`,
    boxShadow: "none",
    fontWeight: "600 !important",
    fontSize: "14px !important",
    minWidth: "100px",
    "&:hover": {
      backgroundColor: theme.palette.primary.main,
      filter: "brightness(0.9)",
      boxShadow: "none",
    },
    "&.Mui-disabled:not([class*='success'])": {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
      opacity: 0.8,
    },
  },

  grid: {
    width: "100%",
    maxWidth: 1200,
    margin: "0 auto",
    "& .react-grid-placeholder": {
      borderRadius: theme.spacing(1.5),
      backgroundColor: theme.palette.primary.main,
      opacity: 0.4,
    },
  },
  cardPaper: {
    position: "relative",
    padding: theme.spacing(0, 2),
    borderRadius: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    height: "100%",
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    transition: "all 0.2s ease-in-out",

    "&:hover": {
      borderColor: theme.palette.primary.main,
    },
  },

  dragHandleArea: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    paddingRight: theme.spacing(1),
    cursor: "move",
    color: theme.palette.text.disabled,
  },

  contentWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    [theme.breakpoints.down("sm")]: {
      alignItems: "column",
    },
  },

  cardHeader: {
    marginBottom: 0,
  },

  customHandle: {
    position: "absolute",
    right: -14,
    top: "50%",
    transform: "translateY(-50%)",
    width: 28,
    height: 28,
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "ew-resize",
    opacity: 0,
    pointerEvents: "none",
    transition: "all 0.2s ease-in-out",
    zIndex: 10,

    "&:hover": {
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
      backgroundColor: ` ${lighten(theme.palette.primary.main, 0.9)}`,
    },
  },
  resizableItem: {
    "&:hover $customHandle": {
      opacity: 1,
      pointerEvents: "auto",
    },
  },

  gridItem: {},
  resizeIcon: {
    color: theme.palette.text.secondary,
  },
  profileBuilderTitle: {
    width: "100%",
    maxWidth: 1200,
    marginBottom: theme.spacing(3),
    marginLeft: theme.spacing(4),
    textAlign: "left",
  },
  builderTitleText: {
    fontWeight: 700,
    marginBottom: theme.spacing(0.5),
  },
}));
const ResponsiveGridLayout = WidthProvider(Responsive);

export interface CoachClientProfileLayoutSettingsProps
  extends Omit<ContainerProps, "children"> {}

export const CoachClientProfileLayoutSettings = (
  props: CoachClientProfileLayoutSettingsProps,
) => {
  const s = useStyles();
  const { showToastAlert } = useToastAlert();

  const [openClientsDialog, setOpenClientsDialog] = useState(false);
  const [preview, setPreview] = usePreview();
  const {
    id: workspaceId,
    nutritionsEnabled,
    clientProfilePositions,
  } = useCurrentWorkspace();
  const [selectedClientUsername, setSelectedClientUsername] = useState(null);
  const isResettingLayoutRef = useRef(false);

  const { mutate: updateProfileCardsPositions, status } =
    useUpdateProfileCardsPosition();

  const [_, setQueryParams] = useQueryParams();

  const parsedPositions: ProfileCardPosition[] = useMemo(
    () => (clientProfilePositions ? JSON.parse(clientProfilePositions) : []),
    [clientProfilePositions],
  );

  const generateInitialLayout = (
    nutritionsEnabled?: boolean,
    parsedPositions?: ProfileCardPosition[],
  ): LayoutItem[] =>
    CLIENT_PROFILE_LAYOUT_SETUP_CARDS_DATA(
      nutritionsEnabled,
      parsedPositions && parsedPositions.length > 0 ? parsedPositions : null,
    )
      .map((card) => ({
        i: card.type,
        x: card.x,
        y: card.y,
        w: card.columns,
        h: card.height,
        minW: card.resizable ? 1 : card.columns,
        maxW: card.resizable ? 2 : card.columns,
        isResizable: card.resizable,
      }))
      .sort((a, b) => a.y - b.y || a.x - b.x);

  const initialLayout = useMemo(
    () => generateInitialLayout(nutritionsEnabled, parsedPositions),
    [nutritionsEnabled, parsedPositions],
  );

  const [layout, setLayout] = useState<LayoutItem[]>(initialLayout);
  const defaultLayout = useMemo(
    () => generateInitialLayout(nutritionsEnabled),
    [nutritionsEnabled],
  );

  const isDirty = useMemo(
    () => !areLayoutsEqual(layout, initialLayout),
    [layout, initialLayout],
  );

  const isDefault = useMemo(
    () => areLayoutsEqual(layout, defaultLayout),
    [layout, defaultLayout],
  );

  const normalizedPreviewLayout = normalizeLayoutForCompare(layout);

  const previewPositions: ProfileCardPosition[] = normalizedPreviewLayout.map(
    (card) => ({
      type: card.i as ProfileCardType,
      x: card.x,
      y: card.y,
      columns: card.w,
    }),
  );

  const handleResizeStop = (_: any, __: any, item: any) => {
    const width = item.w >= 2 ? 2 : 1;
    setLayout((prev) =>
      prev.map((l) => (l.i === item.i ? { ...l, w: width, h: 2 } : l)),
    );
  };

  const handleSelect = (_event: React.MouseEvent, client?: ClientInfoDto) => {
    if (!client?.username) return;

    setQueryParams({ preview: "true" });
    setSelectedClientUsername(client.username);
    setPreview(PreviewType.WEB);
    setOpenClientsDialog(false);
  };

  const resetToDefaultLayout = () => {
    isResettingLayoutRef.current = true;
    setLayout(defaultLayout);

    showToastAlert("info", {
      message: "Layout reset to default",
    });
  };

  const handleSaveLayout = React.useCallback(() => {
    const jsonPositions = JSON.stringify(previewPositions);

    updateProfileCardsPositions(
      {
        id: workspaceId,
        clientProfilePositions: jsonPositions,
      },
      {
        onSuccess: () => {
          showToastAlert("success", {
            message: "Layout saved successfully!",
          });
        },
        onError: () => {
          showToastAlert("error", {
            message: "Failed to save layout",
          });
        },
      },
    );
  }, [previewPositions, updateProfileCardsPositions, workspaceId]);

  const handleLayoutChange = (newLayout: LayoutItem[]) => {
    if (isResettingLayoutRef.current) {
      isResettingLayoutRef.current = false;
      return;
    }
    setLayout(newLayout);
  };
  const handleDiscard = () => {
    setLayout(initialLayout);
    showToastAlert("info", {
      message: "Changes discarded",
    });
  };
  const CustomResizeHandle = (handleAxis: string, ref: any) => {
    if (handleAxis !== "e") return null;

    return (
      <div ref={ref} className={s.customHandle}>
        <ChevronsLeftRight size={16} />
      </div>
    );
  };

  useNavigationGuard(
    isDirty,
    "Client builder still has unsaved changes",
    "Save or discard your changes",
  );

  return (
    <>
      {!preview && (
        <Container className={s.root}>
          <Box className={s.profileBuilderTitle}>
            <Typography variant="h5" className={s.builderTitleText}>
              Client Profile Layout
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Configure how the profile looks for your clients. New layout will
              be applied to dashboards of all clients.
            </Typography>
          </Box>
          <Box className={s.actionsWrapper}>
            <Box className={s.actionsBar}>
              <Box className={s.leftActions}>
                <Button
                  onClick={() => setOpenClientsDialog(true)}
                  className={s.textBtn}
                  startIcon={<Eye size={24} />}
                >
                  <span className={s.buttonText}>Preview</span>
                </Button>
                <Button
                  onClick={resetToDefaultLayout}
                  className={s.textBtn}
                  startIcon={<RotateCcw size={24} />}
                  disabled={isDefault}
                >
                  <span className={s.buttonText}>Reset to default layout</span>
                </Button>
              </Box>

              <Box className={s.rightActions}>
                <Button
                  className={s.btnDiscard}
                  disabled={!isDirty}
                  onClick={handleDiscard}
                  startIcon={<X size={24} />}
                >
                  Discard changes
                </Button>
                <LoadingActionButtonWithStatus
                  onClick={handleSaveLayout}
                  variant="contained"
                  status={status}
                  className={s.btnSave}
                  disabled={!isDirty}
                >
                  Save layout
                </LoadingActionButtonWithStatus>
              </Box>
            </Box>
          </Box>

          <ResponsiveGridLayout
            className={clsx(s.grid, "layout")}
            layouts={{
              lg: layout,
              md: layout,
              sm: layout,
              xs: layout,
              xxs: layout,
            }}
            cols={{ lg: 2, md: 2, sm: 2, xs: 2, xxs: 2 }}
            rowHeight={25}
            draggableHandle=".drag-handle"
            resizeHandles={["e"]}
            resizeHandle={CustomResizeHandle}
            onLayoutChange={handleLayoutChange}
            onResizeStop={handleResizeStop}
            margin={[16, 16]}
            verticalCompact
            preventCollision={false}
          >
            {CLIENT_PROFILE_LAYOUT_SETUP_CARDS_DATA(nutritionsEnabled).map(
              (card) => {
                const isDisabled =
                  card.type === ProfileCardType.NUTRITION && !nutritionsEnabled;
                const isResizable = card.resizable;
                return (
                  <div
                    key={card.type}
                    className={clsx(s.gridItem, isResizable && s.resizableItem)}
                  >
                    <Paper elevation={0} className={s.cardPaper}>
                      <Box className={clsx("drag-handle", s.dragHandleArea)}>
                        <GripVertical size={24} />
                      </Box>
                      <Box className={s.contentWrapper}>
                        {card.withCardHeader && (
                          <ClientDetailsCardHeader
                            icon={
                              <ProfileIconBlock
                                icon={<card.Icon />}
                                bgVariant={"accent"}
                                size="small"
                              />
                            }
                            subtitle={card.subtitle}
                            title={card.title}
                            className={s.cardHeader}
                          />
                        )}
                        {card.content && card.content}
                      </Box>
                    </Paper>
                  </div>
                );
              },
            )}
          </ResponsiveGridLayout>
          {openClientsDialog && (
            <React.Suspense fallback={<></>}>
              <ClientDialog
                onSelect={handleSelect}
                open={openClientsDialog}
                onClose={() => setOpenClientsDialog(false)}
              />
            </React.Suspense>
          )}
        </Container>
      )}
      {preview && selectedClientUsername && (
        <ProfilePreview
          username={selectedClientUsername}
          positions={previewPositions}
        />
      )}
    </>
  );
};
